import React, { useState, useEffect, useCallback, useMemo } from "react";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import { GridColDef } from "@mui/x-data-grid-premium";
import { Box, CircularProgress, Backdrop, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSnackbar } from "notistack";
import { IsolationRequestsRows, IsolationRequestRow } from "./models/rows";
import { SelectOption } from "src/components/core-components/mui/ByzMuiMultiSelect";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzMuiOpenProcessMenu from "src/components/core-components/mui/ByzMuiOpenProcessMenu";
import { DetailPanel } from "./detail-grid";
import { useTranslate } from "src/locales";
import IsolationSelectionDialog, { IsolationOption } from "./IsolationSelectionDialog";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";
import { useBaseMutation, useBaseMutationWithVariables } from "src/api/core/useBaseQuery";
import { baseService } from "src/api/core/baseService";
import { statusLabelMap, isolationStatusOptions } from "./models/enums";
import ByzCard from "src/components/core-components/ByzCard";
import ByzDataGridToolbar from "src/components/core-components/mui/data-grid/toolbar";
import ByzDeleteModal from "src/components/core-components/mui/ByzDeleteModal";
import ByzFilterCard from "src/components/core-components/ByzFilterCard";
import { FilterValues } from "src/components/core-components/ByzFilterCard/types";
import { dateTimeFormatter } from "src/utils/date/dateHelper";

const getDetailPanelContent = ({ row }: any) => {
  return <DetailPanel data={row} />;
};

function IsolationRequestsList() {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isTabletDown = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isProcessingBulk, setIsProcessingBulk] = useState(false);

  // Filter state
  const [filterValues, setFilterValues] = useState<FilterValues>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 1)),
    endDate: new Date(),
    status: [{ label: "Gönderildi", value: "1" }],
  });

  const [isolationSelectionOpen, setIsolationSelectionOpen] = useState<boolean>(false);
  const [rows, setRows] = useState<IsolationRequestRow[]>(IsolationRequestsRows);
  const [selectedRows, setSelectedRows] = useState<{ [key: number]: boolean }>(
    Object.fromEntries(IsolationRequestsRows.map((row) => [row.id, false]))
  );

  const [approveCancelState, setApproveCancelState] = useState({
    open: false,
    rowId: "",
    status: ""
  });

  const [rejectModalState, setRejectModalState] = useState({
    open: false,
    rowId: 0,
    status: ""
  });

  // Filter değişikliklerini handle et
  const handleFilterChange = useCallback((values: FilterValues) => {
    setFilterValues(values);
  }, []);

  // API için request data - her filterValues değiştiğinde güncellenir
  const filterRequestData = useMemo(() => ({
    FirstDate: filterValues.startDate,
    LastDate: filterValues.endDate,
    StatusList: filterValues.status?.length
      ? filterValues.status.map(x => String(x.value))
      : ["1"] // Default: Gönderildi
  }), [filterValues.startDate, filterValues.endDate, filterValues.status]);

  const { mutate: getAllIsolationsRequestMutate, data: allIsolationsRequestData, isPending: isLoadingList } = useBaseMutationWithVariables<any, any>({
    endpoint: "/isolation/api/PatientIsolations/GetAllPatientsIsolationRequests",
    method: "POST",
  });

  type UpdateStatusPayload = { pieId: string; status: string };

  const { mutateAsync: updateIsolationStatusAsync } =
    useBaseMutationWithVariables<any, UpdateStatusPayload>({
      endpoint: "/isolation/api/PatientIsolations/UpdateIsolationRequestStatus",
      method: "PUT",
    });

  const { mutate: updateIsolationStatusMutate } =
    useBaseMutationWithVariables<any, UpdateStatusPayload>({
      endpoint: "/isolation/api/PatientIsolations/UpdateIsolationRequestStatus",
      method: "PUT",
    });



  // Onay iptal (Optimistic Update)
  const handleOpenCancelApprovalDialog = (rowId: any, status: string) => {
    const numRowId = Number(rowId);

    // Önceki durumu kaydet
    const previousStatus = rows.find(r => r.id === numRowId)?.approve_status;

    // Hemen UI'ı güncelle (optimistic)
    setRows(prevRows => prevRows.map(row => {
      if (row.id === numRowId) {
        return { ...row, approve_status: status };
      }
      return row;
    }));

    // API çağrısı
    updateIsolationStatusMutate(
      { pieId: rowId.toString(), status },
      {
        onError: () => {
          // Hata olursa eski haline geri al
          setRows(prevRows => prevRows.map(row => {
            if (row.id === numRowId) {
              return { ...row, approve_status: previousStatus || "2" };
            }
            return row;
          }));
        },
      }
    );
  };

  // Dialog onSuccess callback (ByzDeleteConfirmDialog için)
  const handleCancelApprovalSuccess = () => {
    setApproveCancelState({ ...approveCancelState, open: false });
  };

  const handleSelectIsolations = (selectedIsolations: IsolationOption[]) => {
    console.log("Selected isolations:", selectedIsolations);
  };

  const handleSelectRow = (id: number) => {
    setSelectedRows((prev) => {
      const newSelectedState = { ...prev, [id]: !prev[id] };
      return newSelectedState;
    });
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSelectedState = Object.fromEntries(
      Object.keys(selectedRows).map((id) => [Number(id), event.target.checked])
    );
    setSelectedRows(newSelectedState);
  };

  // İzolasyon isteğini onaylar (Optimistic Update)
  const handleApprove = (rowId: number, status: string) => {
    // Önceki durumu kaydet (hata durumunda geri almak için)
    const previousStatus = rows.find(r => r.id === rowId)?.approve_status;

    // Hemen UI'ı güncelle (optimistic)
    setRows(prevRows => prevRows.map(row => {
      if (row.id === rowId) {
        return { ...row, approve_status: status };
      }
      return row;
    }));

    // API çağrısı
    updateIsolationStatusMutate(
      { pieId: rowId.toString(), status },
      {
        onError: () => {
          // Hata olursa eski haline geri al
          setRows(prevRows => prevRows.map(row => {
            if (row.id === rowId) {
              return { ...row, approve_status: previousStatus || "1" };
            }
            return row;
          }));
        },
      }
    );
  };

  // İzolasyon isteğini reddetmek için modal açar
  const handleReject = (rowId: number, status: string) => {
    setRejectModalState({
      open: true,
      rowId: rowId,
      status: status
    });
  };

  // İzolasyon isteğini reddeder (Optimistic Update)
  const handleConfirmReject = (explanation?: string) => {
    const rowId = rejectModalState.rowId;
    const status = rejectModalState.status;

    // Önceki durumu kaydet
    const previousStatus = rows.find(r => r.id === rowId)?.approve_status;

    // Hemen UI'ı güncelle (optimistic)
    setRows(prevRows => prevRows.map(row => {
      if (row.id === rowId) {
        return { ...row, approve_status: status };
      }
      return row;
    }));

    // Modal'ı kapat
    setRejectModalState({
      open: false,
      rowId: 0,
      status: ""
    });

    // API çağrısı
    updateIsolationStatusMutate(
      { pieId: rowId.toString(), status },
      {
        onError: () => {
          // Hata olursa eski haline geri al
          setRows(prevRows => prevRows.map(row => {
            if (row.id === rowId) {
              return { ...row, approve_status: previousStatus || "1" };
            }
            return row;
          }));
        },
      }
    );
  };

  const handleProcessAction = async (action: string) => {
    const selectedIds = Object.keys(selectedRows)
      .filter(id => selectedRows[Number(id)])
      .map(id => Number(id));

    if (selectedIds.length === 0) {
      enqueueSnackbar(t("isolation-requests.select-at-least-one") || "Lütfen en az bir kayıt seçin!", { variant: 'warning' });
      return;
    }

    const statusForAction =
      action === "Onayla" ? "2" : // 2 = Onaylandı
        action === "Reddet" ? "3" : // 3 = Reddedildi
          null;

    if (!statusForAction) return;

    setIsProcessingBulk(true);
    try {
      // Tüm istekleri paralel olarak gönder
      await Promise.all(
        selectedIds.map(id =>
          updateIsolationStatusAsync({ pieId: id.toString(), status: statusForAction })
        )
      );

      enqueueSnackbar(
        action === "Onayla"
          ? t("isolation-requests.all-approved-success") || "Tüm seçili istekler başarıyla onaylandı."
          : t("isolation-requests.all-rejected-success") || "Tüm seçili istekler başarıyla reddedildi.",
        { variant: 'success' }
      );

      // Listeyi yenile
      handleFilter();
      // Seçimleri temizle
      setSelectedRows(Object.fromEntries(rows.map(row => [row.id, false])));

    } catch (error) {
      console.error(`${action} all failed:`, error);
      enqueueSnackbar(t("isolation-requests.bulk-action-error") || "Toplu işlem sırasında bir hata oluştu!", { variant: 'error' });
    } finally {
      setIsProcessingBulk(false);
    }
  };

  const handleSendSingle = (rowId: number) => {
    handleApprove(rowId, "1"); // 1 = Gönderildi
  };

  const handleDeleteSingle = async (rowId: number) => {
    try {
      await baseService.delete("isolation/api/PatientIsolations/DeletePatientIsolationRequest/", rowId);
      // Silinen satırı listeden çıkar
      setRows(prev => prev.filter(row => row.id !== rowId));
    } catch (err) {
      console.error("Delete isolation request error", err);
      alert("İzolasyon isteği silinirken hata oluştu!");
    }
  };

  // Filtre uygula
  const handleFilter = useCallback(() => {
    getAllIsolationsRequestMutate(filterRequestData);
  }, [getAllIsolationsRequestMutate, filterRequestData]);

  // Filtreleri temizle
  const handleClearFilters = useCallback(() => {
    setFilterValues({
      startDate: new Date(new Date().setDate(new Date().getDate() - 1)),
      endDate: new Date(),
      status: [],
    });
  }, []);

  // NOT: Otomatik istek yok - sadece search butonuna basınca istek atılır

  useEffect(() => {
    if (allIsolationsRequestData && Array.isArray(allIsolationsRequestData)) {
      // API'den gelen satırları grid modeline map'le
      const mappedRows: IsolationRequestRow[] = allIsolationsRequestData.map((row: any) => ({
        id: Number(row.pieid ?? row.id ?? 0),
        selection: false,
        icon: row.icon,
        isolationtypename: row.isolationtypename,
        patientnamesurname: row.patientnamesurname,
        departmentname: row.departmentname,
        doctorname: row.doctorname,
        hospitalbedname: row.hospitalbedname,
        start_date: row.start_date,
        finish_date: row.finish_date,
        approve_status: row.approve_status?.toString?.() ?? "0",
        app_or_reject_date: row.app_or_reject_date,
        app_user: row.app_user,
      }));

      setRows(mappedRows);
      const initialSelectedRows = Object.fromEntries(
        mappedRows.map((row: IsolationRequestRow) => [row.id, false])
      );
      setSelectedRows(initialSelectedRows);
    }
  }, [allIsolationsRequestData]);

  const getColumnWidth = useCallback(
    (mobileWidth: number, tabletWidth: number, desktopFlex = 1) =>
      isMobile
        ? { width: mobileWidth, minWidth: mobileWidth }
        : isTabletDown
          ? { width: tabletWidth, minWidth: tabletWidth }
          : { flex: desktopFlex, minWidth: tabletWidth },
    [isMobile, isTabletDown]
  );

  const columns: GridColDef[] = [
    {
      field: "selection",
      headerName: "",
      width: 80,
      sortable: false,
      renderHeader: () => (
        <ByzMuiCheckBox
          checked={Object.values(selectedRows).every(Boolean) && Object.values(selectedRows).length > 0}
          indeterminate={
            Object.values(selectedRows).some(Boolean) &&
            !Object.values(selectedRows).every(Boolean)
          }
          onChange={handleSelectAll}
        />
      ),
      renderCell: (params) => (
        <ByzMuiCheckBox
          checked={selectedRows[params.row.id] || false}
          onChange={() => handleSelectRow(params.row.id)}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      renderHeader: () => (
        <ByzMuiOpenProcessMenu
          menuItems={[
            {
              label: t("buttons.approve-all"),
              iconColor: "success",
              onClick: () => handleProcessAction("Onayla"),
            },
            {
              label: t("buttons.reject-all"),
              iconColor: "error",
              onClick: () => handleProcessAction("Reddet"),
            },
          ]}
        />
      ),
      renderCell: (params) => {
        // İzolasyon durumuna göre butonların durumlarını belirleme
        const isApproved = params.row.approve_status === "2";
        const isPending = params.row.approve_status === "1";

        return (
          <ByzMuiOpenProcessMenu
            menuItems={[
              {
                label: t("buttons.approve"),
                iconColor: "success",
                onClick: () => handleApprove(params.row.id, "2"),
                disabled: !isPending // Sadece Gönderildi durumunda aktif olacak
              },
              {
                label: t("buttons.reject"),
                iconColor: "error",
                onClick: () => handleReject(params.row.id, "3"),
                disabled: !isPending // Sadece Gönderildi durumunda aktif olacak
              },
              {
                label: t("buttons.approve-cancel"),
                onClick: () => handleOpenCancelApprovalDialog(params.row.id, "0"),
                disabled: !isApproved // Sadece Onaylandı durumunda aktif olacak
              },
              {
                label: t("buttons.open-request-form"),
                onClick: () => {
                  //const rowData = rows.find(row => row.id === params.row.id) || null;
                  //setCurrentIsolation(rowData);
                  setIsolationSelectionOpen(true);
                }
                // Her zaman aktif
              },
            ]}
          />
        );
      },
    },
    {
      field: "icon",
      headerName: "İkon",
      width: isMobile ? 110 : 120,
      minWidth: isMobile ? 110 : 120,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              width={30}
              height={30}
              src={`data:image/svg+xml;base64,${params.row.icon ?? params.row.pics_svg}`}
              alt="My SVG"
            />
          </Box>
        );
      },
    },
    {
      field: "isolationtypename",
      headerName: t("isolation-requests.columns.isolation-type"),
      ...getColumnWidth(170, 190),
    },
    {
      field: "patientnamesurname",
      headerName: t("isolation-requests.columns.patient-name"),
      ...getColumnWidth(180, 210),
    },
    {
      field: "departmentname",
      headerName: t("isolation-requests.columns.department"),
      ...getColumnWidth(150, 180),
    },
    {
      field: "doctorname",
      headerName: t("isolation-requests.columns.doctor"),
      ...getColumnWidth(150, 180),
    },
    {
      field: "hospitalbedname",
      headerName: t("isolation-requests.columns.bed"),
      width: isMobile ? 100 : 110,
      minWidth: isMobile ? 100 : 110,
    },
    {
      field: "start_date",
      headerName: t("isolation-requests.columns.start-time"),
      ...getColumnWidth(155, 170),
      valueFormatter: (value) => dateTimeFormatter(value, "dd.MM.yyyy HH:mm")
    },
    {
      field: "finish_date",
      headerName: t("isolation-requests.columns.end-time"),
      ...getColumnWidth(155, 170),
      valueFormatter: (value) => dateTimeFormatter(value, "dd.MM.yyyy HH:mm")
    },
    {
      field: "approve_status",
      headerName: t("isolation-requests.columns.approval-status"),
      ...getColumnWidth(150, 170),
      renderCell: (params) => {
        let bgColor = 'transparent';

        // Status 1 = Gönderildi
        if (params.value === "1") {
          bgColor = "#AEADA64D";
        }
        // Status 2 = Onaylandı
        else if (params.value === "2") {
          bgColor = "#34C75959";
        }
        // Status 3 = İptal Edildi
        else if (params.value === "3") {
          bgColor = "#C734364D";
        }

        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              padding: "4px",
              backgroundColor: bgColor,
            }}
          >
            {statusLabelMap[params.value] || "-"}
          </Box>
        );
      }
    },
    {
      field: "app_or_reject_date",
      headerName: t("isolation-requests.columns.approval-time"),
      ...getColumnWidth(165, 180),
      valueFormatter: (value) => dateTimeFormatter(value, "dd.MM.yyyy HH:mm")
    },
    {
      field: "app_user",
      headerName: t("isolation-requests.columns.approved-by"),
      ...getColumnWidth(150, 180),
    }
  ];

  return (
    <>
      <ByzFilterCard
        title={t("isolation-requests.filter") || "Filtrele"}
        filters={["dateRange", "status"]}
        onChange={handleFilterChange}
        onFilter={handleFilter}
        onClear={handleClearFilters}
        statusOptions={isolationStatusOptions}
        variant="icon"
      />
      <ByzCard title={t("isolation-requests.isolation-requests") || "İzolasyon İstekleri"}>
        <ByzMuiDataGrid
          columns={columns}
          pageSize={10}
          rows={rows}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={() => 220}
          customToolbar={ByzDataGridToolbar}
          whiteGrid
          sx={{
            "& .MuiDataGrid-columnHeader": {
              py: isMobile ? 0.5 : 0.75,
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              lineHeight: isMobile ? "16px" : "18px",
            },
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              overflow: "visible",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflowX: "auto",
            },
            "& .MuiDataGrid-cell": {
              fontSize: isMobile ? "12px" : "13px",
            },
          }}
        />
      </ByzCard>

      <ByzDeleteConfirmDialog
        open={approveCancelState.open}
        onClose={() => setApproveCancelState({ ...approveCancelState, open: false })}
        patientId={approveCancelState.rowId}
        onSuccess={handleCancelApprovalSuccess}
        title={t("isolation-requests.approval-cancel-title")}
        content={t("isolation-requests.approval-cancel-content")}
      />
      <IsolationSelectionDialog
        open={isolationSelectionOpen}
        onClose={() => setIsolationSelectionOpen(false)}
        onSelect={handleSelectIsolations}
      />
      <ByzDeleteModal
        open={rejectModalState.open}
        onClose={() => setRejectModalState({ open: false, rowId: 0, status: "" })}
        onConfirm={handleConfirmReject}
        title={t("isolation-requests.reject-title") || "İptal Etmek İstediğinize Emin misiniz ?"}
        content={t("isolation-requests.reject-content") || "İptal açıklama girmelisiniz!"}
        explanationLabel={t("isolation-requests.reject-reason") || "Açıklama"}
        cancelText={t("buttons.cancel") || "İptal"}
      />

      {/* Loading Overlay */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 5,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
        open={isLoadingList || isProcessingBulk}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Box sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
            {isProcessingBulk ? t("processing") || "İşleniyor..." : t("loading")}
          </Box>
        </Box>
      </Backdrop>
    </>
  );
}

export default IsolationRequestsList;

